import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MandatoryKpiDetailsDialog from 'dialogs/MandatoryKpiDetailsDialog/MandatoryKpiDetailsDialog';
import useGetTranslatedAttribute from 'hooks/useGetTranslatedAttribute';
import CompulsoryKpi from 'types/CompulsoryKpi';
import { useDialog } from 'utils/dialog';
import snackbar from 'utils/snackbar';

import CustomLoader from '../../components/CustomLoader/CustomLoader';
import Material from '../../types/Material';
import api from '../../utils/api';
import { ActionTableCell, FlexContainer, StyledButton, Title } from './styled';

const MandatoryKpisPage = () => {
    const { t } = useTranslation();
    const getTranslatedAttribute = useGetTranslatedAttribute();

    const [isLoading, setIsLoading] = useState(true);
    const [kpis, setKpis] = useState<Material[]>([]);
    const [selectedKpiId, selectKpiId] = useState(0);

    const [isKpiDialogOpen, setIsKpiDialogOpen] = useState(false);

    const [openDialog, closeDialog] = useDialog();

    const getData = useCallback(() => {
        api.request('/compulsory-kpis').then((res) => {
            setKpis(res);
            setIsLoading(false);
        });
    }, []);

    const openKpiDialog = useCallback((kpiId: number) => {
        selectKpiId(kpiId);
        setIsKpiDialogOpen(true);
    }, [selectKpiId, setIsKpiDialogOpen]);

    const closeKpiDialog = useCallback((kpiId?: number) => {
        if (kpiId) {
            selectKpiId(kpiId);
        }
        setIsKpiDialogOpen(false);
    }, [selectKpiId, setIsKpiDialogOpen]);

    useEffect(() => {
        if (!isKpiDialogOpen) getData();
    }, [getData, isKpiDialogOpen]);

    const deleteKpi = useCallback((kpiId: number) => {
        api.request('/compulsory-kpis/' + kpiId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.compulsory_kpi_delete.success'));
            getData();
        });
    }, [getData, t]);

    const deleteKpiConfirm = useCallback((kpi: CompulsoryKpi) => {
        openDialog({
            title: t('dialogs.compulsory_kpi_delete.title'),
            message: t('dialogs.compulsory_kpi_delete.message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteKpi(kpi.id);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteKpi, openDialog, t]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>{t('pages.mandatory_kpis.title')}</Title>
                {/*
                <GreenButton variant='outlined' onClick={() => openKpiDialog(0)}>
                    {t('pages.mandatory_kpis.new_kpi')}
                </GreenButton>
                */}
            </FlexContainer>
            <TableContainer component={Paper} style={{ marginTop: '20px' }} className='book-list-table-view'>
                <Table aria-label='simple table'>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('pages.mandatory_kpis.table.description')}</TableCell>
                            <TableCell>{t('pages.generic.table.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {kpis.map(kpi => {
                            if (!kpi.enabled) return null;

                            return (
                                <TableRow key={kpi.id}>
                                    <TableCell>{getTranslatedAttribute(kpi, 'description')}</TableCell>
                                    <ActionTableCell>
                                        <StyledButton variant='outlined' color='primary' onClick={() => openKpiDialog(kpi.id)}>
                                            {t('pages.generic.table.details')}
                                        </StyledButton>
                                        {kpi.description === 'Deviazioni' && (
                                            <StyledButton variant='outlined' color='secondary' onClick={() => deleteKpiConfirm(kpi)}>
                                                {t('pages.languages.table.delete')}
                                            </StyledButton>
                                        )}
                                    </ActionTableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <MandatoryKpiDetailsDialog open={isKpiDialogOpen} onClose={closeKpiDialog} kpiId={selectedKpiId} />
        </>
    );
};

export default MandatoryKpisPage;
